import GradientBorderRadius from "@/ui/gradient-border-radius";
import { useWalletDiscoveryFilter } from "@providers/wallet-discovery-filter/wallet-discovery-filter-provider.tsx";
import {
  useBulkDeleteWallets,
  useGetLowRoiWalletAddresses,
} from "@api/wallet-management";
import {
  BulkDeleteWalletsParams,
  LowRoiWallet,
} from "@api/wallet-management/types.ts";
import { useQueryClient } from "@tanstack/react-query";
import { WALLET_SCREENER_KEY } from "@api/wallet-screener/wallet-screener.ts";

import { Modal } from "@/ui/modals";
import { Button } from "@/ui/button";
import { useState } from "react";

const WalletsAdminControls = () => {
  const { fetchedWallets, selectedWallets, setSelectedWallets } =
    useWalletDiscoveryFilter();
  const { data: lowRoiWallets } = useGetLowRoiWalletAddresses(25);
  const { mutate } = useBulkDeleteWallets();
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false);
  const [deleteMode, setDeleteMode] = useState<"selected" | "lowRoi" | null>(
    null,
  );

  const handleSelectAll = () => {
    const updated = new Map<string, boolean>();
    fetchedWallets.forEach((_chain, address) => {
      updated.set(address, true);
    });

    setSelectedWallets(updated);
  };

  const handleDeselectAll = () => {
    setSelectedWallets(new Map<string, boolean>());
  };

  const handleDeleteWallets = () => {
    const walletsToDelete: BulkDeleteWalletsParams[] = [];

    if (deleteMode === "selected") {
      selectedWallets.forEach((_value, address) => {
        const chain = fetchedWallets.get(address);
        if (chain !== undefined) {
          walletsToDelete.push({ address, chain });
        }
      });
    } else if (deleteMode === "lowRoi" && lowRoiWallets) {
      lowRoiWallets.items.forEach((item: LowRoiWallet) => {
        walletsToDelete.push({
          address: item.address,
          chain: Number(item.chain),
        } as BulkDeleteWalletsParams);
      });
    }

    mutate(
      { walletsToDelete },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: [WALLET_SCREENER_KEY],
            refetchType: "active",
          });
        },
      },
    );
  };

  return (
    <>
      <div className="flex justify-center items-center gap-[0.3125rem]">
        <GradientBorderRadius radius="bigger" hover="50" className="h-full">
          <button
            className="w-[6.25rem] h-[1.375rem] text-center text-[0.75rem] font-semibold flex items-center justify-center"
            onClick={handleSelectAll}
          >
            Select All
          </button>
        </GradientBorderRadius>
        <GradientBorderRadius radius="bigger" hover="50" className="h-full">
          <button
            className="w-[6.25rem] h-[1.375rem] text-center text-[0.75rem] font-semibold flex items-center justify-center"
            onClick={handleDeselectAll}
          >
            Deselect All
          </button>
        </GradientBorderRadius>
        <GradientBorderRadius radius="bigger" hover="50" className="h-full">
          <button
            className="w-[6.25rem] h-[1.375rem] text-center text-[0.75rem] font-semibold flex items-center justify-center"
            onClick={() => {
              if (lowRoiWallets && lowRoiWallets.items.length > 0) {
                setSelectedWallets(new Map<string, boolean>());
                setDeleteMode("lowRoi");
                setIsOpen(true);
              }
            }}
          >
            Delete &lt; 25%
          </button>
        </GradientBorderRadius>

        <GradientBorderRadius radius="bigger" hover="50" className="h-full">
          <button
            className="w-[6.25rem] h-[1.375rem] text-center text-[0.75rem] font-semibold flex items-center justify-center"
            onClick={() => {
              if (selectedWallets.size > 0) {
                setDeleteMode("selected");
                setIsOpen(true);
              }
            }}
          >
            Delete
          </button>
        </GradientBorderRadius>
      </div>

      <Modal
        opened={isOpen}
        onClose={() => {
          setIsOpen(false);
          setDeleteMode(null);
        }}
        className="w-[28.125rem] h-[8rem]  bg-section-gradient"
        size="28rem"
      >
        <Modal.Title
          title="Delete Selected Wallets"
          className="text-xs font-semibold"
        />

        <Modal.Footer>
          <div className="w-full h-[5rem] flex flex-col justify-around items-center">
            <p className="text-xs text-white text-center">
              {deleteMode === "selected"
                ? `Are you sure you want to delete ${selectedWallets.size} selected wallet(s)?`
                : `Are you sure you want to delete ${lowRoiWallets?.count} low ROI wallet(s)?`}
            </p>

            <div className="flex justify-center items-center gap-[0.3125rem]">
              <Button
                active={false}
                title="Yes"
                className="w-[9.375rem] font-semibold text-xs"
                onClick={() => {
                  handleDeleteWallets();
                  setIsOpen(false);
                  setDeleteMode(null);
                }}
              />
              <Button
                onClick={() => setIsOpen(false)}
                active={false}
                title="No"
                className="w-[9.375rem] font-semibold text-xs"
              />
            </div>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default WalletsAdminControls;
